/**
 * MessaggiList — elenca i messaggi inviati al cliente.
 *
 * Replica del mobile `Notifications.js`:
 *  - ordinati dal più recente al più vecchio
 *  - card per ogni messaggio: pallino cyan se non letto, check grigio se letto
 *  - chip "NUOVO" + CTA "Segna come letto" sui non letti
 *  - click sulla card non letta → mark as read (optimistic + backend)
 *  - empty state se nessun messaggio
 */

import { useMemo } from 'react'
import { CheckCheck, Inbox } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { formatBackendDate, parseBackendDate } from '@/lib/dates'
import type { Messaggio } from '@/lib/graphql/types'
import { cn } from '@/lib/utils'

type Props = {
  messages: Messaggio[] | undefined
  isLoading: boolean
  onMarkRead: (message: Messaggio) => void
}

export function MessaggiList({ messages, isLoading, onMarkRead }: Props) {
  const sorted = useMemo(() => {
    if (!Array.isArray(messages)) return []
    return [...messages].sort((a, b) => {
      const ta = parseBackendDate(a.datetime)?.valueOf() ?? new Date(a.datetime).valueOf() ?? 0
      const tb = parseBackendDate(b.datetime)?.valueOf() ?? new Date(b.datetime).valueOf() ?? 0
      return tb - ta
    })
  }, [messages])

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Caricamento messaggi…</p>
  }

  if (sorted.length === 0) {
    return (
      <EmptyState
        icon={<Inbox className="h-5 w-5" />}
        title="Nessun messaggio"
        description="Le comunicazioni dirette a te compariranno qui."
      />
    )
  }

  return (
    <ul className="flex flex-col gap-3">
      {sorted.map((msg, idx) => (
        <li key={`${msg.datetime}-${idx}`}>
          <MessageCard message={msg} onMarkRead={onMarkRead} />
        </li>
      ))}
    </ul>
  )
}

function MessageCard({
  message,
  onMarkRead,
}: {
  message: Messaggio
  onMarkRead: (m: Messaggio) => void
}) {
  const isUnread = !message.letto
  const dateLabel = formatDateAndTime(message.datetime)

  return (
    <Card
      className={cn(
        'p-5 transition-colors',
        isUnread && 'border-secondary/40',
      )}
    >
      <div className="flex items-center gap-2">
        {isUnread ? (
          <span className="h-2 w-2 shrink-0 rounded-full bg-secondary" aria-hidden />
        ) : (
          <CheckCheck className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span
          className={cn(
            'flex-1 truncate text-xs font-medium',
            isUnread ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {dateLabel}
        </span>
        {isUnread && (
          <span className="rounded-full bg-secondary/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-secondary">
            Nuovo
          </span>
        )}
      </div>

      <p
        className={cn(
          'mt-3 text-sm leading-relaxed',
          isUnread ? 'text-foreground' : 'text-foreground/85',
        )}
      >
        {message.testo || ''}
      </p>

      {isUnread && (
        <button
          type="button"
          onClick={() => onMarkRead(message)}
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-secondary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
        >
          <CheckCheck className="h-3.5 w-3.5" />
          Segna come letto
        </button>
      )}
    </Card>
  )
}

function formatDateAndTime(datetime: string): string {
  if (!datetime) return ''
  // Prova prima il parser backend (DD/MM/YYYY), poi nativo (ISO).
  const parsed = parseBackendDate(datetime)
  if (parsed) return parsed.format('DD MMMM YYYY · HH:mm')
  const d = new Date(datetime)
  if (!isNaN(d.getTime())) {
    return d
      .toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
      .replace(',', ' ·')
  }
  return formatBackendDate(datetime)
}
